/**
 * Inspectable Catchball routing inheritance (build contract §2.4a / canon 29 §6).
 */

import type { CatchballRoutingInheritanceBreakdown } from '../../../types/prioritization/catchballRouting';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';

const KEY_LABELS: Record<string, string> = {
  rolloutEnabled: 'Rollout (fan-out) enabled',
  ballSplitUnit: 'Split unit',
  ballSplitMaxChildren: 'Max children per split',
  requireAggregateBeforePassUp: `Require ${CATCHBALL_LABELS.combineResponses.toLowerCase()} before pass-up`,
  resolutionTarget: 'Resolution target',
  pathAdjustPolicy: 'Path adjust policy',
};

const SOURCE_LABELS: Record<string, string> = {
  canonical: 'Canonical default',
  workflow: 'Workflow / template',
  cycle: 'Cycle override',
  track: 'Track override',
};

/**
 * Raw enum and boolean values were rendered straight from the row — a holder
 * doing their job saw `false`, `org_unit`, `split_node` and
 * `holder_remaining_spine` on the main work screen. Values are spelled in
 * English here; the key stays canonical for anyone reading the code.
 */
const VALUE_LABELS: Record<string, string> = {
  true: 'On',
  false: 'Off',
  org_unit: 'Org unit',
  split_node: 'Split node',
  holder_remaining_spine: "Holder's remaining path",
  path_spine: 'Path spine',
};

function valueLabel(value: unknown): string {
  const raw = String(value);
  return VALUE_LABELS[raw] ?? raw;
}

interface CatchballRoutingPolicyPanelProps {
  breakdown: CatchballRoutingInheritanceBreakdown;
  compact?: boolean;
}

export function CatchballRoutingPolicyPanel({
  breakdown,
  compact = false,
}: CatchballRoutingPolicyPanelProps) {
  /**
   * Collapsed by default. This is configuration read once a quarter, and it was
   * sitting open on the screen where someone passes a ball. The summary line
   * carries the only part that changes what the holder can do today — whether
   * fan-out is available.
   */
  const overrides = breakdown.rows.filter((row) => row.source !== 'canonical').length;

  return (
    <details
      className={` border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] ${
        compact ? 'p-3' : 'p-4'
      }`}
    >
      <summary className="cursor-pointer list-none flex items-baseline justify-between gap-3">
        <h4 className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">
          How this ball routes
        </h4>
        <span className="text-xs text-[var(--text-ter)]">
          {breakdown.effective.rolloutEnabled ? 'Fan out available' : 'Fan out off'}
          {overrides > 0 ? ` · ${overrides} override${overrides === 1 ? '' : 's'}` : ''}
        </span>
      </summary>
      <div className="space-y-1.5 mt-3">
        {breakdown.rows.map((row) => (
          <div key={row.key} className="flex items-baseline justify-between gap-3 text-xs">
            <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {KEY_LABELS[row.key] ?? row.key}
            </span>
            <span className="text-right">
              <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                {valueLabel(row.value)}
              </span>
              <span className="ml-2 text-[var(--text-ter)]">← {SOURCE_LABELS[row.source] ?? row.source}</span>
            </span>
          </div>
        ))}
      </div>
      {!breakdown.effective.rolloutEnabled && (
        <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] mt-2">
          Rollout is off for this cycle — Fan out stays hidden until the workflow or
          cycle template enables it.
        </p>
      )}
    </details>
  );
}
